import api from "../api/api";
import IAttachment, { IAttachmentResponseModel, mapAttachmentResponseModel } from "../interfaces/IAttachment";
import { GeoCoordinate } from "../interfaces/GeoCoordinate";
import IGeoCoordinate from "../interfaces/IGeoCoordinate";
import ISaleComp, { ISaleCompRequestModel, ISaleCompResponseModel, ISaleCompState } from "../interfaces/ISaleComp";
import { parseDateTime } from "../utils/utils";

export default class SaleCompsApiService {
    public async getSaleComps(): Promise<ISaleComp[]> {
        const response = await api.get<ISaleCompResponseModel[]>("/salecomp/get");
        return this.mapResponseModelsToSaleComps(response.data);
    }

    public async createSaleComp(saleCompState: ISaleCompState, pipedriveBuyerId: number | null, pipedriveSellerId: number | null, pipedriveBrokerageId: number | null, centerPoint: IGeoCoordinate, regridId: string | null): Promise<{ id: number, modifiedDate: Date, modifiedByUser: string }> {
        const response = await api.post("/salecomp/create", this.mapSaleCompStateToRequestModel(saleCompState, pipedriveBuyerId, pipedriveSellerId, pipedriveBrokerageId, centerPoint, regridId));

        return {
            id: response.data.id,
            modifiedDate: parseDateTime(response.data.modifiedDate),
            modifiedByUser: response.data.modifiedByUser,
        };
    }

    public async updateSaleComp(saleComp: ISaleComp): Promise<{ modifiedDate: Date, modifiedByUser: string }> {
        const response = await api.post("/salecomp/update", this.mapSaleCompToRequestModel(saleComp));

        return {
            modifiedDate: parseDateTime(response.data.modifiedDate),
            modifiedByUser: response.data.modifiedByUser,
        };
    }

    public async deleteSaleComp(id: number): Promise<void> {
        await api.delete(`/salecomp/delete?id=${id}`);
    }

    public async uploadSaleCompAttachment(saleCompId: number, file: File): Promise<IAttachment> {
        const formData = new FormData();
        formData.append("file", file);

        const response = await api.post<IAttachmentResponseModel>(`/salecomp/uploadAttachment?id=${saleCompId}`, formData, {
            headers: { "Content-Type": "multipart/form-data" },
        });

        return mapAttachmentResponseModel(response.data);
    }

    public async deleteSaleCompAttachment(saleCompId: number, key: string): Promise<void> {
        await api.delete(`/salecomp/deleteAttachment?id=${saleCompId}&key=${encodeURIComponent(key)}`);
    }

    private mapResponseModelsToSaleComps(data: ISaleCompResponseModel[]): ISaleComp[] {
        return data.map(sc => {
            return {
                id: sc.id,
                address: sc.address,
                regridId: sc.regridId,
                saleDate: parseDateTime(sc.saleDate),
                salePrice: sc.salePrice,
                grossPerAcre: sc.grossPerAcre,
                zoning: sc.zoning,
                buildingSize: sc.buildingSize,
                parcelSizeAcres: sc.parcelSizeAcres,
                landSizeUsable: sc.landSizeUsable,
                pipedriveBuyerId: sc.pipedriveBuyerId,
                buyerName: sc.buyerName,
                pipedriveSellerId: sc.pipedriveSellerId,
                sellerName: sc.sellerName,
                notes: sc.notes,
                centerPoint: new GeoCoordinate(sc.latitude, sc.longitude),
                propertyType: sc.propertyType,
                dealProfile: sc.dealProfile,
                noi: sc.noi,
                capRate: sc.capRate,
                isMarketed: sc.marketed || false,
                pipedriveBrokerageId: sc.pipedriveBrokerageId,
                brokerageName: sc.pipedriveBrokerage,
                modifiedByUser: sc.modifiedByUser,
                modifiedDate: parseDateTime(sc.modifiedDate),
                buildingPerSqFt: sc.buildingPerSqFt,
                pricePerUsableAcre: sc.pricePerUsableAcre,
                compSource: sc.compSource,
                attachments: (sc.attachments ?? []).map(mapAttachmentResponseModel),
            };
        });
    }

    private mapSaleCompStateToRequestModel(saleCompState: ISaleCompState, pipedriveBuyerId: number | null, pipedriveSellerId: number | null, pipedriveBrokerageId: number | null, centerPoint: IGeoCoordinate, regridId: string | null): ISaleCompRequestModel {
        return {
            address: saleCompState.address!,
            regridId: regridId,
            saleDate: saleCompState.saleDate!.toISOString(),
            salePrice: saleCompState.salePrice!,
            grossPerAcre: saleCompState.grossPerAcre!,
            zoning: saleCompState.zoning,
            buildingSize: saleCompState.buildingSize,
            parcelSizeAcres: saleCompState.parcelSizeAcres!,
            landSizeUsable: saleCompState.landSizeUsable!,
            pipedriveBuyerId: pipedriveBuyerId,
            buyerName: saleCompState.buyerName,
            pipedriveSellerId: pipedriveSellerId,
            sellerName: saleCompState.sellerName,
            notes: saleCompState.notes,
            latitude: centerPoint.latitude,
            longitude: centerPoint.longitude,
            propertyType: saleCompState.propertyType,
            dealProfile: saleCompState.dealProfile,
            noi: saleCompState.noi,
            capRate: saleCompState.capRate,
            marketed: saleCompState.isMarketed,
            pipedriveBrokerageId: pipedriveBrokerageId,
            pipedriveBrokerage: saleCompState.brokerageName,
            buildingPerSqFt: saleCompState.buildingPerSqFt,
            pricePerUsableAcre: saleCompState.pricePerUsableAcre,
            compSource: saleCompState.compSource,
        };
    }

    private mapSaleCompToRequestModel(saleComp: ISaleComp): ISaleCompRequestModel {
        return {
            id: saleComp.id,
            address: saleComp.address,
            regridId: saleComp.regridId,
            saleDate: saleComp.saleDate.toISOString(),
            salePrice: saleComp.salePrice,
            grossPerAcre: saleComp.grossPerAcre,
            zoning: saleComp.zoning,
            buildingSize: saleComp.buildingSize,
            parcelSizeAcres: saleComp.parcelSizeAcres,
            landSizeUsable: saleComp.landSizeUsable,
            pipedriveBuyerId: saleComp.pipedriveBuyerId,
            buyerName: saleComp.buyerName,
            pipedriveSellerId: saleComp.pipedriveSellerId,
            sellerName: saleComp.sellerName,
            notes: saleComp.notes,
            latitude: saleComp.centerPoint.latitude,
            longitude: saleComp.centerPoint.longitude,
            propertyType: saleComp.propertyType,
            dealProfile: saleComp.dealProfile,
            noi: saleComp.noi,
            capRate: saleComp.capRate,
            marketed: saleComp.isMarketed,
            pipedriveBrokerageId: saleComp.pipedriveBrokerageId,
            pipedriveBrokerage: saleComp.brokerageName,
            buildingPerSqFt: saleComp.buildingPerSqFt,
            pricePerUsableAcre: saleComp.pricePerUsableAcre,
            compSource: saleComp.compSource,
        };
    }
}